import React, { useEffect, useState, useRef } from 'react';
import type { MapData, MapHotspot } from '../types';
import { Breadcrumbs } from '../components/Breadcrumbs';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  MapPin,
  Compass,
  Layers,
  ChevronRight,
} from 'lucide-react';

interface MapsAtlasPageProps {
  mapSlug?: string;
}

export const MapsAtlasPage: React.FC<MapsAtlasPageProps> = ({ mapSlug = 'echocondria' }) => {
  const [map, setMap] = useState<MapData | null>(null);
  const [allMaps, setAllMaps] = useState<MapData[]>([]);
  const [activeHotspot, setActiveHotspot] = useState<MapHotspot | null>(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`/api/maps/${mapSlug}`).then((r) => r.json() as Promise<MapData>),
      fetch('/api/maps').then((r) => r.json() as Promise<MapData[]>),
    ])
      .then(([mapData, mapsList]) => {
        setMap(mapData);
        setAllMaps(Array.isArray(mapsList) ? mapsList : [mapData]);
        if (mapData && mapData.hotspots && mapData.hotspots.length > 0) {
          setActiveHotspot(mapData.hotspots[0]);
        }
      })
      .catch((e) => console.error('Error fetching map:', e))
      .finally(() => setLoading(false));
  }, [mapSlug]);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.5, map?.maxScale || 4));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.5, map?.minScale || 0.8));
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const breadcrumbs = [
    { label: 'Home', url: '/' },
    { label: 'Maps', url: '/maps' },
    { label: map?.title || 'Atlas' },
  ];

  return (
    <div className="maps-atlas-container">
      <section className="page-hero py-4">
        <div className="container">
          <Breadcrumbs items={breadcrumbs} />
          <p className="eyebrow mb-1">
            {map?.eyebrow || 'City Atlas'}
          </p>
          <h1 className="hero-title display-5 fw-bold mb-2">{map?.title || 'Echocondria Atlas'}</h1>
          <p className="lead text-secondary small mb-0">{map?.description}</p>
        </div>
      </section>

      <div className="container py-4">
        {loading ? (
          <div className="py-5 text-center text-secondary">
            <div className="spinner-border text-primary me-2" role="status"></div>
            Loading atlas viewer...
          </div>
        ) : !map ? (
          <div className="alert alert-danger">Map not found.</div>
        ) : (
          <div className="row g-4">
            {/* Interactive Canvas */}
            <div className="col-lg-8">
              <div className="card shadow-sm border rounded-4 overflow-hidden position-relative">
                {/* Map Floating Toolbar */}
                <div className="position-absolute top-0 end-0 m-3 z-3 btn-group btn-group-sm shadow">
                  <button type="button" className="btn btn-outline-secondary" onClick={handleZoomOut} title="Zoom Out">
                    <ZoomOut size={16} />
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={handleZoomIn} title="Zoom In">
                    <ZoomIn size={16} />
                  </button>
                  <button type="button" className="btn btn-outline-secondary fw-bold" onClick={handleReset} title="Fit to Screen">
                    <Maximize2 size={16} />
                  </button>
                </div>

                {/* Viewport & Drag Canvas */}
                <div
                  ref={containerRef}
                  className="atlas-viewport position-relative overflow-hidden cursor-grab"
                  style={{
                    height: '600px',
                    cursor: isDragging ? 'grabbing' : 'grab',
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <div
                    className="atlas-canvas position-absolute"
                    style={{
                      transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                      transformOrigin: 'center center',
                      transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <img
                      src={map.image.src}
                      alt={map.image.alt}
                      className="w-100 h-100 object-fit-contain user-select-none pointer-events-none"
                      draggable={false}
                    />

                    {/* Hotspot Markers */}
                    {(map.hotspots || []).map((spot) => (
                      <button
                        key={spot.slug}
                        type="button"
                        className={`atlas-hotspot-pin position-absolute btn btn-sm p-0 rounded-circle ${
                          activeHotspot?.slug === spot.slug ? 'active' : ''
                        }`}
                        style={{
                          left: `${spot.x}%`,
                          top: `${spot.y}%`,
                          transform: 'translate(-50%, -50%)',
                          zIndex: activeHotspot?.slug === spot.slug ? 10 : 5,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveHotspot(spot);
                        }}
                        title={spot.title}
                      >
                        <span className="hotspot-pulse"></span>
                        <span className="hotspot-icon bg-primary text-white p-2 rounded-circle shadow d-inline-flex">
                          <MapPin size={14} />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar Landmark Details */}
            <div className="col-lg-4">
              <div className="d-flex flex-column gap-3">
                {/* Active Landmark Focus Card */}
                {activeHotspot ? (
                  <div className="card border shadow-sm rounded-4 p-4">
                    <p className="eyebrow mb-1">
                      {activeHotspot.eyebrow || 'Landmark'}
                    </p>
                    <h4 className="fw-bold mb-2">{activeHotspot.title}</h4>
                    <p className="text-secondary small mb-3">{activeHotspot.description}</p>
                    {activeHotspot.chips && activeHotspot.chips.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mb-0">
                        {activeHotspot.chips.map((chip, idx) => (
                          <span key={idx} className="badge bg-secondary-subtle small">
                            {chip}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="card border rounded-4 p-4 text-center text-secondary">
                    <Compass size={32} className="mx-auto mb-2 text-secondary" />
                    <p className="small mb-0">Click any hotspot on the map to inspect landmark details.</p>
                  </div>
                )}

                {/* All Landmarks List */}
                <div className="card border shadow-sm rounded-4 p-3">
                  <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                    <Layers size={16} /> Landmarks ({map.hotspots?.length || 0})
                  </h6>
                  <div className="list-group list-group-flush max-h-350 overflow-y-auto">
                    {(map.hotspots || []).map((spot) => (
                      <button
                        key={spot.slug}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex align-items-center justify-content-between p-2 rounded-3 mb-1 border-0 ${
                          activeHotspot?.slug === spot.slug ? 'bg-primary-subtle text-primary fw-semibold' : ''
                        }`}
                        onClick={() => setActiveHotspot(spot)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <MapPin size={14} className={activeHotspot?.slug === spot.slug ? 'text-primary' : 'text-secondary'} />
                          <span className="small">{spot.title}</span>
                        </div>
                        <ChevronRight size={14} className="text-secondary" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
